import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import { StyledDialog } from 'theme/index';

import type { DocumentReview } from '../types/documentReview.types';

interface DocumentDetailsDialogProps {
  open: boolean;
  document: DocumentReview | null;
  loading?: boolean;
  onClose: () => void;
  onViewPdf: () => void;
  onViewImages: () => void;
}

const labelSx = {
  fontWeight: 700,
  fontSize: '0.95rem',
};

const valueSx = {
  fontWeight: 400,
  fontSize: '0.875rem',
};

const DocumentDetailsDialog: React.FC<DocumentDetailsDialogProps> = ({
  open,
  document,
  loading = false,
  onClose,
  onViewPdf,
  onViewImages,
}) => {
  let dialogContent: React.ReactNode;

  if (loading) {
    dialogContent = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  } else if (document) {
    dialogContent = (
      <Stack spacing={2.5}>
        <Typography sx={valueSx}>
          <Box component="span" sx={labelSx}>
            Patient:
          </Box>{' '}
          {document.patientName}
        </Typography>

        <Typography sx={valueSx}>
          <Box component="span" sx={labelSx}>
            Upload Date:
          </Box>{' '}
          {dayjs(document.uploadedDate).format('MM/DD/YYYY hh:mm A')}
        </Typography>

        <Typography sx={valueSx}>
          <Box component="span" sx={labelSx}>
            Document:
          </Box>{' '}
          {document.documentName}
        </Typography>

        <Typography sx={valueSx}>
          <Box component="span" sx={labelSx}>
            Description:
          </Box>{' '}
          {document.documentDescription ?? '—'}
        </Typography>

        {document.rejectionReason && (
          <Typography sx={valueSx}>
            <Box component="span" sx={labelSx}>
              Rejection Reason:
            </Box>{' '}
            {document.rejectionReason}
          </Typography>
        )}
      </Stack>
    );
  } else {
    dialogContent = (
      <Typography color="text.secondary">
        Unable to load document details.
      </Typography>
    );
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Document Details
      </DialogTitle>

      <DialogContent>{dialogContent}</DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {document && (document.pdfAvailable ?? document.generatedPdfUrl) && (
          <Button variant="outlined" size="small" onClick={onViewPdf}>
            View PDF
          </Button>
        )}

        {document &&
          (document.rawImagesAvailable ?? document.rawImages.length > 0) && (
            <Button variant="outlined" size="small" onClick={onViewImages}>
              View Images (
              {document.rawImageCount ?? document.rawImages.length})
            </Button>
          )}

        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default DocumentDetailsDialog;
